/**
 * Widget Content Components
 * Individual widget content for each generic widget type
 */

import { useState, useEffect, useCallback } from 'react';
import { cn } from '../../../lib/utils';
import { Skeleton } from '../../ui/skeleton';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import {
  Check,
  Plus,
  Trash2,
  Calendar,
  ExternalLink,
  Maximize2,
  Minimize2,
  RefreshCw,
  AlertCircle,
  Info,
  Link2,
  Image as ImageIcon,
  Building2,
  Users,
  Shield,
  Activity,
} from 'lucide-react';

// Helper function for fetch with timeout
const fetchWithTimeout = async (url, options = {}, timeoutMs = 15000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error('Request timed out');
    }
    throw err;
  }
};

// Shared loading component
const WidgetLoading = () => (
  <div className="space-y-3">
    <Skeleton className="h-8 w-24" />
    <Skeleton className="h-4 w-32" />
    <Skeleton className="h-2 w-full" />
  </div>
);

/**
 * Custom Text Widget Content
 */
export const CustomTextContent = ({ config, onConfigChange }) => {
  const [localText, setLocalText] = useState(config?.text || '');
  const [localTitle, setLocalTitle] = useState(config?.title || '');

  useEffect(() => {
    setLocalText(config?.text || '');
    setLocalTitle(config?.title || '');
  }, [config]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({ title: localTitle, text: localText });
    }
  };

  if (!config?.text && !config?.title) {
    return (
      <div className="flex flex-col h-full gap-3">
        <Input
          placeholder="Widget Title (optional)"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          className="text-sm"
        />
        <Textarea
          placeholder="Enter your text content here..."
          value={localText}
          onChange={(e) => setLocalText(e.target.value)}
          className="flex-1 min-h-[80px] text-sm resize-none"
        />
        <Button size="sm" onClick={handleSave} className="self-end">
          <Check className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {config?.title && (
        <h4 className="font-semibold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {config.title}
        </h4>
      )}
      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{config?.text || 'No content'}</p>
    </div>
  );
};

/**
 * Custom Metric Widget Content
 */
export const CustomMetricContent = ({ config, onConfigChange }) => {
  const [editing, setEditing] = useState(!config?.value);
  const [localValue, setLocalValue] = useState(config?.value || '');
  const [localLabel, setLocalLabel] = useState(config?.label || '');
  const [localSublabel, setLocalSublabel] = useState(config?.sublabel || '');

  useEffect(() => {
    setLocalValue(config?.value || '');
    setLocalLabel(config?.label || '');
    setLocalSublabel(config?.sublabel || '');
  }, [config]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({ value: localValue, label: localLabel, sublabel: localSublabel });
    }
    setEditing(false);
  };

  if (editing || !config?.value) {
    return (
      <div className="flex flex-col h-full gap-2">
        <Input
          placeholder="Value (e.g., 42, $1,200, 98%)"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          className="text-lg font-bold"
        />
        <Input
          placeholder="Label (e.g., Active Users)"
          value={localLabel}
          onChange={(e) => setLocalLabel(e.target.value)}
          className="text-sm"
        />
        <Input
          placeholder="Sublabel (optional)"
          value={localSublabel}
          onChange={(e) => setLocalSublabel(e.target.value)}
          className="text-xs"
        />
        <Button size="sm" onClick={handleSave} className="self-end mt-auto">
          <Check className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full justify-center cursor-pointer" onClick={() => setEditing(true)}>
      <p className="text-4xl font-bold tracking-tight text-primary mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {config.value}
      </p>
      <p className="text-sm text-muted-foreground">{config.label || 'Custom Metric'}</p>
      {config.sublabel && <p className="text-xs text-muted-foreground mt-1">{config.sublabel}</p>}
    </div>
  );
};

/**
 * Custom API Metric Widget Content
 * Fetches data from a user-specified API endpoint
 */
export const CustomApiMetricContent = ({ config, onConfigChange }) => {
  const [editing, setEditing] = useState(!config?.apiUrl);
  const [localUrl, setLocalUrl] = useState(config?.apiUrl || '');
  const [localLabel, setLocalLabel] = useState(config?.label || '');
  const [localJsonPath, setLocalJsonPath] = useState(config?.jsonPath || '');
  const [localHeaders, setLocalHeaders] = useState(config?.headers || '');
  const [localRefreshInterval, setLocalRefreshInterval] = useState(config?.refreshInterval || '60');
  
  const [fetchedValue, setFetchedValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  useEffect(() => {
    setLocalUrl(config?.apiUrl || '');
    setLocalLabel(config?.label || '');
    setLocalJsonPath(config?.jsonPath || '');
    setLocalHeaders(config?.headers || '');
    setLocalRefreshInterval(config?.refreshInterval || '60');
  }, [config]);

  // Function to extract value from JSON using a path like "data.count" or "results[0].value"
  const extractValue = useCallback((data, path) => {
    if (!path) {
      // Try common field names
      if (data.value !== undefined) return data.value;
      if (data.count !== undefined) return data.count;
      if (data.total !== undefined) return data.total;
      if (data.result !== undefined) return data.result;
      if (typeof data === 'number' || typeof data === 'string') return data;
      return JSON.stringify(data);
    }
    
    try {
      const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
      let result = data;
      for (const part of parts) {
        result = result[part];
      }
      return result;
    } catch {
      return null;
    }
  }, []);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    if (!config?.apiUrl) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const headers = {};
      if (config?.headers) {
        try {
          const parsed = JSON.parse(config.headers);
          Object.assign(headers, parsed);
        } catch {
          // If not valid JSON, try key:value format
          config.headers.split('\n').forEach(line => {
            const [key, value] = line.split(':').map(s => s.trim());
            if (key && value) headers[key] = value;
          });
        }
      }
      
      const response = await fetch(config.apiUrl, {
        headers: {
          'Accept': 'application/json',
          ...headers,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const value = extractValue(data, config?.jsonPath);
      setFetchedValue(value);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message);
      setFetchedValue(null);
    } finally {
      setIsLoading(false);
    }
  }, [config?.apiUrl, config?.headers, config?.jsonPath, extractValue]);

  // Auto-refresh
  useEffect(() => {
    if (config?.apiUrl && !editing) {
      fetchData();
      
      const interval = parseInt(config?.refreshInterval || '60') * 1000;
      if (interval > 0) {
        const timer = setInterval(fetchData, interval);
        return () => clearInterval(timer);
      }
    }
  }, [config?.apiUrl, config?.refreshInterval, editing, fetchData]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({
        apiUrl: localUrl,
        label: localLabel,
        jsonPath: localJsonPath,
        headers: localHeaders,
        refreshInterval: localRefreshInterval,
      });
    }
    setEditing(false);
  };

  if (editing || !config?.apiUrl) {
    return (
      <div className="flex flex-col h-full gap-2 overflow-auto">
        <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium mb-1">How to use:</p>
              <p>Enter your API URL. The response should be JSON. Use JSON Path to extract specific values (e.g., <code className="bg-muted px-1 rounded">data.count</code> or <code className="bg-muted px-1 rounded">results[0].value</code>).</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label className="text-xs">API URL *</Label>
          <Input
            placeholder="https://api.example.com/metrics"
            value={localUrl}
            onChange={(e) => setLocalUrl(e.target.value)}
            className="text-sm font-mono"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-xs">Label</Label>
          <Input
            placeholder="Metric Name"
            value={localLabel}
            onChange={(e) => setLocalLabel(e.target.value)}
            className="text-sm"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-xs">JSON Path (optional)</Label>
          <Input
            placeholder="data.value or leave empty for auto-detect"
            value={localJsonPath}
            onChange={(e) => setLocalJsonPath(e.target.value)}
            className="text-sm font-mono"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-xs">Headers (optional, JSON or key:value per line)</Label>
          <Textarea
            placeholder={'{"Authorization": "Bearer token"}\nor\nAuthorization: Bearer token'}
            value={localHeaders}
            onChange={(e) => setLocalHeaders(e.target.value)}
            className="text-xs font-mono h-16 resize-none"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-xs">Refresh Interval (seconds)</Label>
          <Input
            type="number"
            min="10"
            placeholder="60"
            value={localRefreshInterval}
            onChange={(e) => setLocalRefreshInterval(e.target.value)}
            className="text-sm"
          />
        </div>
        
        <Button size="sm" onClick={handleSave} className="self-end mt-auto" disabled={!localUrl}>
          <Check className="w-4 h-4 mr-1" /> Save & Fetch
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full justify-center">
      {isLoading && !fetchedValue ? (
        <WidgetLoading />
      ) : error ? (
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-destructive mx-auto mb-2" />
          <p className="text-sm text-destructive">{error}</p>
          <Button size="sm" variant="ghost" onClick={fetchData} className="mt-2">
            <RefreshCw className="w-4 h-4 mr-1" /> Retry
          </Button>
        </div>
      ) : (
        <div className="cursor-pointer" onClick={() => setEditing(true)}>
          <div className="flex items-center gap-2 mb-1">
            {isLoading && <RefreshCw className="w-3 h-3 animate-spin text-muted-foreground" />}
          </div>
          <p className="text-4xl font-bold tracking-tight text-primary mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {fetchedValue !== null ? String(fetchedValue) : '—'}
          </p>
          <p className="text-sm text-muted-foreground">{config.label || 'API Metric'}</p>
          {lastFetched && (
            <p className="text-xs text-muted-foreground mt-1">
              Updated {lastFetched.toLocaleTimeString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Custom Progress Widget Content
 */
export const CustomProgressContent = ({ config, onConfigChange }) => {
  const [editing, setEditing] = useState(!config?.current);
  const [localCurrent, setLocalCurrent] = useState(config?.current || '0');
  const [localTarget, setLocalTarget] = useState(config?.target || '100');
  const [localLabel, setLocalLabel] = useState(config?.label || '');

  useEffect(() => {
    setLocalCurrent(config?.current || '0');
    setLocalTarget(config?.target || '100');
    setLocalLabel(config?.label || '');
  }, [config]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({ current: localCurrent, target: localTarget, label: localLabel });
    }
    setEditing(false);
  };

  const percent = config?.target ? Math.round((parseFloat(config.current || 0) / parseFloat(config.target)) * 100) : 0;

  if (editing || !config?.current) {
    return (
      <div className="flex flex-col h-full gap-2">
        <Input
          placeholder="Label (e.g., Q1 Sales Goal)"
          value={localLabel}
          onChange={(e) => setLocalLabel(e.target.value)}
          className="text-sm"
        />
        <div className="flex gap-2">
          <Input
            placeholder="Current"
            type="number"
            value={localCurrent}
            onChange={(e) => setLocalCurrent(e.target.value)}
            className="text-sm"
          />
          <span className="self-center text-muted-foreground">/</span>
          <Input
            placeholder="Target"
            type="number"
            value={localTarget}
            onChange={(e) => setLocalTarget(e.target.value)}
            className="text-sm"
          />
        </div>
        <Button size="sm" onClick={handleSave} className="self-end mt-auto">
          <Check className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    );
  }

  const getColor = () => {
    if (percent >= 70) return 'text-[rgb(var(--success))]';
    if (percent >= 40) return 'text-[rgb(var(--warning))]';
    return 'text-primary';
  };

  return (
    <div className="flex flex-col h-full justify-center cursor-pointer" onClick={() => setEditing(true)}>
      {config.label && (
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{config.label}</p>
      )}
      <p className={cn("text-4xl font-bold tracking-tight mb-2", getColor())} style={{ fontFamily: 'Chivo, sans-serif' }}>
        {percent}%
      </p>
      <p className="text-sm text-muted-foreground">
        {config.current} / {config.target}
      </p>
      <div className="mt-2 h-1.5 bg-muted/30 rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-500",
            percent >= 70 ? "bg-[rgb(var(--success))]" : percent >= 40 ? "bg-[rgb(var(--warning))]" : "bg-primary"
          )}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Custom Checklist Widget Content
 */
export const CustomChecklistContent = ({ config, onConfigChange }) => {
  const [items, setItems] = useState(config?.items || []);
  const [newItem, setNewItem] = useState('');
  const [title, setTitle] = useState(config?.title || 'My Checklist');

  useEffect(() => {
    setItems(config?.items || []);
    setTitle(config?.title || 'My Checklist');
  }, [config]);

  const addItem = () => {
    if (!newItem.trim()) return;
    const updatedItems = [...items, { id: Date.now(), text: newItem, checked: false }];
    setItems(updatedItems);
    setNewItem('');
    if (onConfigChange) {
      onConfigChange({ title, items: updatedItems });
    }
  };

  const toggleItem = (id) => {
    const updatedItems = items.map(item =>
      item.id === id ? { ...item, checked: !item.checked } : item
    );
    setItems(updatedItems);
    if (onConfigChange) {
      onConfigChange({ title, items: updatedItems });
    }
  };

  const removeItem = (id) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    if (onConfigChange) {
      onConfigChange({ title, items: updatedItems });
    }
  };

  const completedCount = items.filter(i => i.checked).length;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {title}
        </h4>
        <span className="text-xs text-muted-foreground">
          {completedCount}/{items.length}
        </span>
      </div>

      <div className="flex-1 space-y-2 overflow-auto">
        {items.map(item => (
          <div key={item.id} className="flex items-center gap-2 group">
            <Checkbox
              checked={item.checked}
              onCheckedChange={() => toggleItem(item.id)}
            />
            <span className={cn(
              "flex-1 text-sm",
              item.checked && "line-through text-muted-foreground"
            )}>
              {item.text}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => removeItem(item.id)}
            >
              <Trash2 className="w-3 h-3 text-destructive" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mt-3 pt-3 border-t border-border/50">
        <Input
          placeholder="Add item..."
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addItem()}
          className="text-sm h-8"
        />
        <Button size="sm" onClick={addItem} className="h-8 px-2">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Custom Countdown Widget Content
 */
export const CustomCountdownContent = ({ config, onConfigChange }) => {
  const [editing, setEditing] = useState(!config?.targetDate);
  const [localDate, setLocalDate] = useState(config?.targetDate || '');
  const [localLabel, setLocalLabel] = useState(config?.label || '');
  const [daysRemaining, setDaysRemaining] = useState(0);

  useEffect(() => {
    setLocalDate(config?.targetDate || '');
    setLocalLabel(config?.label || '');
  }, [config]);

  useEffect(() => {
    if (config?.targetDate) {
      const target = new Date(config.targetDate);
      const now = new Date();
      const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
      setDaysRemaining(diff);
    }
  }, [config?.targetDate]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({ targetDate: localDate, label: localLabel });
    }
    setEditing(false);
  };

  if (editing || !config?.targetDate) {
    return (
      <div className="flex flex-col h-full gap-2">
        <Input
          placeholder="Event name (e.g., Product Launch)"
          value={localLabel}
          onChange={(e) => setLocalLabel(e.target.value)}
          className="text-sm"
        />
        <Input
          type="date"
          value={localDate}
          onChange={(e) => setLocalDate(e.target.value)}
          className="text-sm"
        />
        <Button size="sm" onClick={handleSave} className="self-end mt-auto">
          <Check className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    );
  }

  const isPast = daysRemaining < 0;
  const isToday = daysRemaining === 0;

  return (
    <div className="flex flex-col h-full justify-center cursor-pointer" onClick={() => setEditing(true)}>
      {config.label && (
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{config.label}</p>
      )}
      <div className="flex items-baseline gap-1">
        <p className={cn(
          "text-4xl font-bold tracking-tight",
          isPast ? "text-muted-foreground" : isToday ? "text-[rgb(var(--success))]" : "text-primary"
        )} style={{ fontFamily: 'Chivo, sans-serif' }}>
          {isToday ? "Today!" : Math.abs(daysRemaining)}
        </p>
        {!isToday && (
          <span className="text-sm text-muted-foreground">
            {isPast ? 'days ago' : 'days left'}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
        <Calendar className="w-3 h-3" />
        {new Date(config.targetDate).toLocaleDateString()}
      </div>
    </div>
  );
};

/**
 * Custom Notes Widget Content
 */
export const CustomNotesContent = ({ config, onConfigChange }) => {
  const [notes, setNotes] = useState(config?.notes || '');

  useEffect(() => {
    setNotes(config?.notes || '');
  }, [config]);

  const handleBlur = () => {
    if (onConfigChange && notes !== config?.notes) {
      onConfigChange({ notes });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Textarea
        placeholder="Type your notes here..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={handleBlur}
        className="flex-1 min-h-0 text-sm resize-none border-0 bg-transparent p-0 focus-visible:ring-0"
        style={{ fontFamily: 'Inter, sans-serif' }}
      />
    </div>
  );
};

/**
 * Quick Links Widget Content
 */
export const QuickLinksContent = ({ config, onConfigChange }) => {
  const [links, setLinks] = useState(config?.links || []);
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [title, setTitle] = useState(config?.title || 'Quick Links');

  useEffect(() => {
    setLinks(config?.links || []);
    setTitle(config?.title || 'Quick Links');
  }, [config]);

  const addLink = () => {
    if (!newLabel.trim() || !newUrl.trim()) return;
    const updatedLinks = [...links, { id: Date.now(), label: newLabel, url: newUrl }];
    setLinks(updatedLinks);
    setNewLabel('');
    setNewUrl('');
    if (onConfigChange) {
      onConfigChange({ title, links: updatedLinks });
    }
  };

  const removeLink = (id) => {
    const updatedLinks = links.filter(link => link.id !== id);
    setLinks(updatedLinks);
    if (onConfigChange) {
      onConfigChange({ title, links: updatedLinks });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <h4 className="font-semibold text-sm mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {title}
      </h4>

      <div className="flex-1 space-y-2 overflow-auto">
        {links.map(link => (
          <div key={link.id} className="flex items-center gap-2 group">
            <Link2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-sm text-primary hover:underline truncate"
            >
              {link.label}
            </a>
            <ExternalLink className="w-3 h-3 text-muted-foreground" />
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => removeLink(link.id)}
            >
              <Trash2 className="w-3 h-3 text-destructive" />
            </Button>
          </div>
        ))}
        {links.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">No links added yet</p>
        )}
      </div>

      <div className="space-y-2 mt-3 pt-3 border-t border-border/50">
        <Input
          placeholder="Link label"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          className="text-sm h-8"
        />
        <div className="flex gap-2">
          <Input
            placeholder="https://..."
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addLink()}
            className="text-sm h-8"
          />
          <Button size="sm" onClick={addLink} className="h-8 px-2">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Image Widget Content
 */
export const ImageWidgetContent = ({ config, onConfigChange }) => {
  const [editing, setEditing] = useState(!config?.imageUrl);
  const [localUrl, setLocalUrl] = useState(config?.imageUrl || '');
  const [localAlt, setLocalAlt] = useState(config?.altText || '');

  useEffect(() => {
    setLocalUrl(config?.imageUrl || '');
    setLocalAlt(config?.altText || '');
  }, [config]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({ imageUrl: localUrl, altText: localAlt });
    }
    setEditing(false);
  };

  if (editing || !config?.imageUrl) {
    return (
      <div className="flex flex-col h-full gap-3">
        <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground flex items-start gap-2">
          <ImageIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>Enter a direct URL to an image (PNG, JPG, GIF, SVG).</p>
        </div>
        <Input
          placeholder="https://example.com/image.png"
          value={localUrl}
          onChange={(e) => setLocalUrl(e.target.value)}
          className="text-sm"
        />
        <Input
          placeholder="Alt text (optional)"
          value={localAlt}
          onChange={(e) => setLocalAlt(e.target.value)}
          className="text-sm"
        />
        <Button size="sm" onClick={handleSave} className="self-end mt-auto" disabled={!localUrl}>
          <Check className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full cursor-pointer" onClick={() => setEditing(true)}>
      <img
        src={config.imageUrl}
        alt={config.altText || 'Widget image'}
        className="max-w-full max-h-full object-contain rounded"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
      <div className="hidden flex-col items-center gap-2 text-muted-foreground">
        <AlertCircle className="w-8 h-8" />
        <p className="text-sm">Image failed to load</p>
      </div>
    </div>
  );
};

/**
 * Power BI Report Widget Content
 */
export const PowerBIReportContent = ({ config, onConfigChange }) => {
  const [editing, setEditing] = useState(!config?.embedCode);
  const [localEmbedCode, setLocalEmbedCode] = useState(config?.embedCode || '');
  const [localTitle, setLocalTitle] = useState(config?.title || '');

  useEffect(() => {
    setLocalEmbedCode(config?.embedCode || '');
    setLocalTitle(config?.title || '');
  }, [config]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({ embedCode: localEmbedCode, title: localTitle });
    }
    setEditing(false);
  };

  // Extract src from iframe if full iframe code is provided
  const getEmbedUrl = () => {
    const code = config?.embedCode || '';
    if (code.includes('<iframe')) {
      const match = code.match(/src=["']([^"']+)["']/);
      return match ? match[1] : '';
    }
    return code;
  };

  if (editing || !config?.embedCode) {
    return (
      <div className="flex flex-col h-full gap-3 overflow-auto">
        <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium mb-1">How to get the embed code:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Open your report in the Power BI service</li>
                <li>Select <strong>File → Embed report → Website or portal</strong></li>
                <li>Copy the iframe code or secure link</li>
                <li>Paste it below</li>
              </ol>
            </div>
          </div>
        </div>
        
        <Input
          placeholder="Report Title (optional)"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          className="text-sm"
        />
        
        <Textarea
          placeholder="Paste your Power BI embed code or URL here..."
          value={localEmbedCode}
          onChange={(e) => setLocalEmbedCode(e.target.value)}
          className="flex-1 min-h-[100px] text-sm font-mono resize-none"
        />
        
        <Button size="sm" onClick={handleSave} className="self-end" disabled={!localEmbedCode}>
          <Check className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    );
  }

  const embedUrl = getEmbedUrl();

  return (
    <div className="flex flex-col h-full">
      {config.title && (
        <h4 className="font-semibold text-sm mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {config.title}
        </h4>
      )}
      <div className="flex-1 min-h-0 rounded overflow-hidden bg-muted/20">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            allowFullScreen
            title={config.title || 'Power BI Report'}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p className="text-sm">Invalid embed code</p>
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        className="self-end mt-2 text-xs"
      >
        Edit embed
      </Button>
    </div>
  );
};

/**
 * Embed Website Widget Content
 */
export const EmbedWebsiteContent = ({ config, onConfigChange }) => {
  const [editing, setEditing] = useState(!config?.url);
  const [localUrl, setLocalUrl] = useState(config?.url || '');
  const [localTitle, setLocalTitle] = useState(config?.title || '');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    setLocalUrl(config?.url || '');
    setLocalTitle(config?.title || '');
  }, [config]);

  // ESC key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({ url: localUrl, title: localTitle });
    }
    setEditing(false);
  };

  if (editing || !config?.url) {
    return (
      <div className="flex flex-col h-full gap-3">
        <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>Enter the URL of the website to embed. Note: Some websites may block iframe embedding due to security policies.</p>
        </div>
        
        <Input
          placeholder="Widget Title (optional)"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          className="text-sm"
        />
        
        <Input
          placeholder="https://example.com"
          value={localUrl}
          onChange={(e) => setLocalUrl(e.target.value)}
          className="text-sm font-mono"
        />
        
        <Button size="sm" onClick={handleSave} className="self-end mt-auto" disabled={!localUrl}>
          <Check className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    );
  }

  // Fullscreen overlay (covers entire viewport)
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-[100] bg-background flex flex-col" data-testid="embed-fullscreen">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 bg-card">
          <h4 className="font-semibold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {config.title || 'Embedded'}
          </h4>
          <div className="flex items-center gap-2">
            <a
              href={config.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
            >
              Open <ExternalLink className="w-3 h-3" />
            </a>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFullscreen(false)}
              className="text-xs gap-1"
              data-testid="embed-exit-fullscreen"
            >
              <Minimize2 className="w-3.5 h-3.5" />
              Exit Fullscreen <span className="text-muted-foreground ml-1">(Esc)</span>
            </Button>
          </div>
        </div>
        <iframe
          src={config.url}
          className="flex-1 w-full border-0"
          title={config.title || 'Embedded website'}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        {config.title ? (
          <h4 className="font-semibold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {config.title}
          </h4>
        ) : <span />}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsFullscreen(true)}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
            title="Expand to fullscreen"
            data-testid="embed-fullscreen-btn"
          >
            <Maximize2 className="w-3 h-3" /> Fullscreen
          </button>
          <a
            href={config.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            Open <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
      <div className="flex-1 min-h-0 rounded overflow-hidden bg-muted/20">
        <iframe
          src={config.url}
          className="w-full h-full border-0"
          title={config.title || 'Embedded website'}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          allowFullScreen
        />
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        className="self-end mt-2 text-xs"
      >
        Edit URL
      </Button>
    </div>
  );
};


/**
 * Jira Dashboard Snapshot Widget Content
 * Renders an auto-refreshing PNG screenshot of a Jira dashboard via the
 * server-side snapshot proxy.
 */
export const JiraDashboardSnapshotContent = ({ config, onConfigChange }) => {
  const [editing, setEditing] = useState(!config?.dashboardId);
  const [localId, setLocalId] = useState(config?.dashboardId || '');
  const [localTitle, setLocalTitle] = useState(config?.title || '');
  const [version, setVersion] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [imgError, setImgError] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const dashboardId = (config?.dashboardId || '').toString().trim();
  const src = dashboardId ? `${API_URL}/api/jira/dashboard-snapshot/${dashboardId}?v=${version}` : null;

  useEffect(() => {
    setLocalId(config?.dashboardId || '');
    setLocalTitle(config?.title || '');
  }, [config]);

  // ESC exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  // Auto-refresh every 5 minutes (matches proxy cache TTL)
  useEffect(() => {
    if (!src) return;
    const t = setInterval(() => setVersion(v => v + 1), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [src]);

  // Fetch snapshot headers (HEAD) to display "last updated"
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(src, { method: 'HEAD' });
        if (!cancelled) setGeneratedAt(r.headers.get('X-Snapshot-Generated-At'));
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [src]);

  const handleSave = () => {
    if (onConfigChange) {
      onConfigChange({ dashboardId: localId.trim(), title: localTitle.trim() });
    }
    setImgError(false);
    setVersion(v => v + 1);
    setEditing(false);
  };

  const handleRefresh = () => {
    setImgError(false);
    setVersion(v => v + 1);
  };

  if (editing || !config?.dashboardId) {
    return (
      <div className="flex flex-col h-full gap-3">
        <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>Enter the Jira dashboard ID (the number in the URL after <code>/dashboards/</code>). The dashboard must be publicly shared in Jira for the snapshot to render.</p>
        </div>

        <Input
          placeholder="Widget Title (optional)"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          className="text-sm"
        />

        <Input
          placeholder="Dashboard ID (e.g. 10114)"
          value={localId}
          onChange={(e) => setLocalId(e.target.value.replace(/[^0-9]/g, ''))}
          className="text-sm font-mono"
          inputMode="numeric"
          data-testid="jira-snapshot-id-input"
        />

        <Button
          size="sm"
          onClick={handleSave}
          className="self-end mt-auto"
          disabled={!localId.trim()}
          data-testid="jira-snapshot-save-btn"
        >
          <Check className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    );
  }

  const lastUpdatedLabel = generatedAt
    ? `Updated ${new Date(generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : null;

  // Build the live Jira URL — defaults to therapymanagement; configurable via env or per-widget
  const jiraBase = (config?.jiraBaseUrl || process.env.REACT_APP_JIRA_BASE_URL || 'https://therapymanagement.atlassian.net').replace(/\/$/, '');
  const liveUrl = `${jiraBase}/jira/dashboards/${dashboardId}`;

  // Fullscreen overlay
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-[100] bg-background flex flex-col" data-testid="jira-snapshot-fullscreen">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 bg-card">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {config.title || `Jira Dashboard ${dashboardId}`}
            </h4>
            {lastUpdatedLabel && <span className="text-xs text-muted-foreground">· {lastUpdatedLabel}</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleRefresh} className="text-xs gap-1" data-testid="jira-snapshot-refresh-fs">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsFullscreen(false)} className="text-xs gap-1" data-testid="jira-snapshot-exit-fs">
              <Minimize2 className="w-3.5 h-3.5" /> Exit Fullscreen <span className="text-muted-foreground ml-1">(Esc)</span>
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-muted/20 flex items-start justify-center p-4">
          {imgError ? (
            <div className="text-center text-muted-foreground p-8">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-[rgb(var(--destructive))]" />
              <p className="text-sm">Failed to load Jira dashboard snapshot.</p>
              <p className="text-xs mt-1">Check that dashboard <code>{dashboardId}</code> is publicly shared in Jira.</p>
            </div>
          ) : (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in Jira"
              className="block"
              data-testid="jira-snapshot-fs-link"
            >
              <img
                src={src}
                alt={`Jira dashboard ${dashboardId}`}
                className="max-w-full h-auto rounded shadow cursor-pointer"
                onError={() => setImgError(true)}
              />
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="font-semibold text-sm truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {config.title || `Jira Dashboard ${dashboardId}`}
          </h4>
          {lastUpdatedLabel && <span className="text-xs text-muted-foreground hidden sm:inline">· {lastUpdatedLabel}</span>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={handleRefresh}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
            title="Refresh now"
            data-testid="jira-snapshot-refresh-btn"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button
            onClick={() => setIsFullscreen(true)}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
            title="Expand to fullscreen"
            data-testid="jira-snapshot-fullscreen-btn"
          >
            <Maximize2 className="w-3 h-3" /> Fullscreen
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 rounded overflow-auto bg-muted/20 flex items-start justify-center">
        {imgError ? (
          <div className="text-center text-muted-foreground p-6">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-[rgb(var(--destructive))]" />
            <p className="text-xs">Failed to load snapshot for dashboard {dashboardId}.</p>
            <button onClick={handleRefresh} className="text-xs text-primary hover:underline mt-1">Try again</button>
          </div>
        ) : (
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in Jira"
            className="block w-full"
            data-testid="jira-snapshot-link"
          >
            <img
              src={src}
              alt={`Jira dashboard ${dashboardId}`}
              className="max-w-full h-auto cursor-pointer"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          </a>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        className="self-end mt-2 text-xs"
      >
        Edit
      </Button>
    </div>
  );
};


/**
 * Azure Cost Widget Content
 * Shows Azure costs broken down by scope with grand total
 * Uses the user's Microsoft SSO token for Azure Management API
 */
export const AzureCostWidgetContent = ({ config, onConfigChange, timeRange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [costData, setCostData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Get Azure Management token from MSAL
  const getAzureToken = async () => {
    try {
      // Import MSAL instance and config
      const { PublicClientApplication } = await import('@azure/msal-browser');
      const { msalConfig, azureManagementRequest } = await import('../../../config/authConfig');
      
      // Get the existing MSAL instance or create one
      const msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        return null;
      }
      
      // Try to get token silently for Azure Management API
      const response = await msalInstance.acquireTokenSilent({
        ...azureManagementRequest,
        account: accounts[0],
      });
      
      return response.accessToken;
    } catch (err) {
      console.warn('Failed to get Azure Management token:', err);
      return null;
    }
  };

  // Fetch Azure costs
  const fetchCosts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    // Convert time range to days (inline to avoid dependency issues)
    let days = 30;
    switch (timeRange) {
      case '7 days': days = 7; break;
      case '30 days': days = 30; break;
      case '90 days': days = 90; break;
      default: days = 30;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Try to get user's Azure token
      const azureToken = await getAzureToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if we have a token
      if (azureToken) {
        headers['Authorization'] = `Bearer ${azureToken}`;
      }
      
      const response = await fetch(`${API_URL}/api/azure/costs/all-scopes?time_range_days=${days}`, {
        headers,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setCostData(data);
        setLastFetched(new Date());
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch Azure costs');
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchCosts();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchCosts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchCosts]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (isLoading && !costData) {
    return (
      <div className="flex flex-col h-full">
        <div className="space-y-3">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-4 w-48" />
          <div className="space-y-2 mt-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-10 h-10 text-destructive mb-3" />
        <p className="text-sm font-medium text-destructive mb-2">Unable to load Azure costs</p>
        <p className="text-xs text-muted-foreground mb-4">{error}</p>
        <Button size="sm" variant="outline" onClick={fetchCosts}>
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with total */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {formatCurrency(costData?.total || 0)}
          </p>
          <p className="text-sm text-muted-foreground">
            Total Azure Spend • {costData?.time_range || 'This period'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={fetchCosts}
          disabled={isLoading}
        >
          <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
        </Button>
      </div>

      {/* Scope breakdown */}
      <div className="flex-1 overflow-auto space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
          Breakdown by Scope ({costData?.scopes?.length || 0})
        </p>
        
        {costData?.scopes?.map((scope, idx) => (
          <div
            key={scope.scope_id || idx}
            className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className={cn(
                "w-2 h-2 rounded-full flex-shrink-0",
                scope.scope_type === 'subscription' ? "bg-blue-500" :
                scope.scope_type === 'managementGroup' ? "bg-purple-500" :
                "bg-gray-500"
              )} />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{scope.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{scope.scope_type?.replace('Group', ' Group')}</p>
              </div>
            </div>
            <p className="text-sm font-mono font-semibold text-right flex-shrink-0 ml-2">
              {formatCurrency(scope.cost)}
            </p>
          </div>
        ))}

        {(!costData?.scopes || costData.scopes.length === 0) && (
          <div className="text-center py-6 text-muted-foreground">
            <p className="text-sm">No cost data available</p>
            <p className="text-xs mt-1">Check your Azure permissions</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="pt-3 mt-3 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {costData?.subscription_count || 0} subscriptions • {costData?.management_group_count || 0} management groups
        </span>
        {lastFetched && (
          <span>Updated {lastFetched.toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
};



/**
 * M365 Licenses Widget Content
 * Shows Microsoft 365 license usage with visual breakdown
 */
export const M365LicensesWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [licenseData, setLicenseData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchLicenses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/licenses`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setLicenseData(data);
        setLastFetched(new Date());
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch license data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLicenses();
    const interval = setInterval(fetchLicenses, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLicenses]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading && !licenseData) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-4 w-48" />
        <div className="space-y-2 mt-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-10 h-10 text-destructive mb-3" />
        <p className="text-sm font-medium text-destructive mb-2">Unable to load licenses</p>
        <p className="text-xs text-muted-foreground mb-4">{error}</p>
        <Button size="sm" variant="outline" onClick={fetchLicenses}>
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  const licenses = licenseData?.licenses || [];
  const totalCost = licenseData?.total_monthly_cost || 0;
  const totalAssigned = licenses.reduce((sum, l) => sum + l.assigned, 0);
  const totalAvailable = licenses.reduce((sum, l) => sum + l.available, 0);

  return (
    <div className="flex flex-col h-full">
      {/* Summary Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {totalAssigned.toLocaleString()}
          </p>
          <p className="text-sm text-muted-foreground">
            Assigned Licenses • {totalAvailable.toLocaleString()} available
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold">{formatCurrency(totalCost)}</p>
          <p className="text-xs text-muted-foreground">Monthly Cost</p>
        </div>
      </div>

      {/* License List */}
      <div className="flex-1 overflow-auto space-y-2">
        {licenses.slice(0, 8).map((license, idx) => {
          const usagePercent = license.total > 0 ? (license.assigned / license.total) * 100 : 0;
          return (
            <div key={license.sku_id || idx} className="p-2 rounded-lg bg-muted/30">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium truncate flex-1 mr-2">{license.sku_name}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {license.assigned}/{license.total}
                </span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all",
                    usagePercent > 90 ? "bg-destructive" :
                    usagePercent > 75 ? "bg-[rgb(var(--warning))]" : "bg-primary"
                  )}
                  style={{ width: `${Math.min(usagePercent, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
        {licenses.length > 8 && (
          <p className="text-xs text-muted-foreground text-center pt-2">
            +{licenses.length - 8} more licenses
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="pt-3 mt-3 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
        <span>{licenses.length} license types</span>
        {lastFetched && <span>Updated {lastFetched.toLocaleTimeString()}</span>}
      </div>
    </div>
  );
};


/**
 * Entra ID Users Widget Content
 * Shows user statistics with member/guest breakdown
 */
export const EntraIDUsersWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/identity`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setUserData(data);
        setLastFetched(new Date());
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch user data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    const interval = setInterval(fetchUsers, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchUsers]);

  if (isLoading && !userData) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-24" />
        <div className="grid grid-cols-2 gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-destructive mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="ghost" onClick={fetchUsers} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Main Stat */}
      <div className="text-center mb-4">
        <p className="text-4xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {userData?.total_users?.toLocaleString() || 0}
        </p>
        <p className="text-sm text-muted-foreground">Total Users</p>
      </div>

      {/* Breakdown Grid */}
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="p-3 rounded-lg bg-blue-500/10 text-center">
          <p className="text-xl font-bold text-blue-500">{userData?.member_users?.toLocaleString() || 0}</p>
          <p className="text-xs text-muted-foreground">Members</p>
        </div>
        <div className="p-3 rounded-lg bg-purple-500/10 text-center">
          <p className="text-xl font-bold text-purple-500">{userData?.guest_users?.toLocaleString() || 0}</p>
          <p className="text-xs text-muted-foreground">Guests</p>
        </div>
        <div className="p-3 rounded-lg bg-green-500/10 text-center">
          <p className="text-xl font-bold text-green-500">{userData?.licensed_users?.toLocaleString() || 0}</p>
          <p className="text-xs text-muted-foreground">Licensed</p>
        </div>
        <div className="p-3 rounded-lg bg-orange-500/10 text-center">
          <p className="text-xl font-bold text-orange-500">{userData?.disabled_accounts?.toLocaleString() || 0}</p>
          <p className="text-xs text-muted-foreground">Disabled</p>
        </div>
      </div>

      {/* Footer */}
      {lastFetched && (
        <div className="pt-2 text-xs text-muted-foreground text-center">
          Updated {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};


/**
 * Security Posture Widget Content
 * Shows security metrics including MFA, CA policies, risky users
 */
export const SecurityPostureWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [securityData, setSecurityData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchSecurity = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/security/posture`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setSecurityData(data);
        setLastFetched(new Date());
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch security data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSecurity();
    const interval = setInterval(fetchSecurity, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSecurity]);

  if (isLoading && !securityData) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-24" />
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-destructive mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="ghost" onClick={fetchSecurity} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const mfaPercent = securityData?.mfa_coverage_percent || 0;
  const getMfaColor = () => {
    if (mfaPercent >= 95) return 'text-green-500';
    if (mfaPercent >= 80) return 'text-[rgb(var(--warning))]';
    return 'text-destructive';
  };

  return (
    <div className="flex flex-col h-full">
      {/* MFA Coverage - Main Metric */}
      <div className="text-center mb-4 pb-4 border-b border-border/50">
        <p className={cn("text-4xl font-bold", getMfaColor())} style={{ fontFamily: 'Chivo, sans-serif' }}>
          {mfaPercent.toFixed(1)}%
        </p>
        <p className="text-sm text-muted-foreground">MFA Coverage</p>
        <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all", getMfaColor().replace('text-', 'bg-'))}
            style={{ width: `${Math.min(mfaPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Security Stats Grid */}
      <div className="grid grid-cols-2 gap-3 flex-1">
        <div className="p-2 rounded-lg bg-muted/30 text-center">
          <p className="text-2xl font-bold text-primary">
            {securityData?.conditional_access_policies ?? '—'}
          </p>
          <p className="text-xs text-muted-foreground">CA Policies</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30 text-center">
          <p className={cn(
            "text-2xl font-bold",
            (securityData?.risky_signins_24h || 0) > 5 ? 'text-destructive' : 'text-green-500'
          )}>
            {securityData?.risky_signins_24h ?? '—'}
          </p>
          <p className="text-xs text-muted-foreground">Risky Sign-ins (24h)</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30 text-center">
          <p className="text-2xl font-bold text-primary">
            {securityData?.privileged_accounts ?? '—'}
          </p>
          <p className="text-xs text-muted-foreground">Admin Roles</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30 text-center">
          <p className="text-2xl font-bold text-primary">
            {securityData?.ca_coverage_percent?.toFixed(0) ?? '—'}%
          </p>
          <p className="text-xs text-muted-foreground">CA Coverage</p>
        </div>
      </div>

      {/* Footer */}
      {lastFetched && (
        <div className="pt-2 text-xs text-muted-foreground text-center">
          Updated {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};


/**
 * M365 Service Health Widget Content
 * Shows status of Microsoft 365 services
 */
export const M365ServiceHealthWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchHealth = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/services/health`);
      
      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
      } else {
        // Fallback to demo data
        setHealthData({
          services: [
            { name: 'Exchange Online', status: 'up' },
            { name: 'SharePoint Online', status: 'up' },
            { name: 'Microsoft Teams', status: 'up' },
            { name: 'Azure AD', status: 'up' },
            { name: 'OneDrive', status: 'up' },
          ],
          data_source: 'demo'
        });
      }
      setLastFetched(new Date());
    } catch (err) {
      setHealthData({
        services: [
          { name: 'Exchange Online', status: 'up' },
          { name: 'SharePoint Online', status: 'up' },
          { name: 'Microsoft Teams', status: 'up' },
          { name: 'Azure AD', status: 'up' },
          { name: 'OneDrive', status: 'up' },
        ],
        data_source: 'demo'
      });
      setLastFetched(new Date());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'up':
      case 'serviceavailable':
      case 'healthy':
        return 'bg-green-500';
      case 'degraded':
      case 'servicedegradation':
        return 'bg-[rgb(var(--warning))]';
      case 'down':
      case 'serviceinterruption':
        return 'bg-destructive';
      default:
        return 'bg-muted-foreground';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'up':
      case 'serviceavailable':
      case 'healthy':
        return 'Healthy';
      case 'degraded':
      case 'servicedegradation':
        return 'Degraded';
      case 'down':
      case 'serviceinterruption':
        return 'Down';
      default:
        return 'Unknown';
    }
  };

  if (isLoading && !healthData) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  const services = healthData?.services || [];
  const healthyCount = services.filter(s => 
    ['up', 'serviceavailable', 'healthy'].includes(s.status?.toLowerCase())
  ).length;

  return (
    <div className="flex flex-col h-full">
      {/* Summary */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-border/50">
        <div>
          <p className="text-sm font-medium">Service Health</p>
          <p className="text-xs text-muted-foreground">
            {healthyCount}/{services.length} services healthy
          </p>
        </div>
        <div className={cn(
          "w-3 h-3 rounded-full",
          healthyCount === services.length ? "bg-green-500" : "bg-[rgb(var(--warning))]"
        )} />
      </div>

      {/* Service List */}
      <div className="flex-1 overflow-auto space-y-1">
        {services.map((service, idx) => (
          <div 
            key={service.id || idx} 
            className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/30 transition-colors"
          >
            <span className="text-sm truncate flex-1">{service.name || service.service}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{getStatusText(service.status)}</span>
              <div className={cn("w-2 h-2 rounded-full", getStatusColor(service.status))} />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="pt-2 text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
        {healthData?.data_source === 'demo' && <span>(Demo)</span>}
        {lastFetched && <span>Updated {lastFetched.toLocaleTimeString()}</span>}
      </div>
    </div>
  );
};


/**
 * Storage Usage Widget Content
 * Shows SharePoint/OneDrive/Exchange storage metrics
 */
export const StorageUsageWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storageData, setStorageData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchStorage = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/storage`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setStorageData(data);
        setLastFetched(new Date());
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch storage data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStorage();
    const interval = setInterval(fetchStorage, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStorage]);

  if (isLoading && !storageData) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-destructive mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="ghost" onClick={fetchStorage} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const sharepoint = storageData?.sharepoint || {};
  const onedrive = storageData?.onedrive || {};
  const exchange = storageData?.exchange || {};

  const StorageBar = ({ label, used, total, color }) => {
    const percent = total > 0 ? (used / total) * 100 : 0;
    return (
      <div className="p-3 rounded-lg bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-xs text-muted-foreground">
            {percent.toFixed(1)}% used
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden mb-1">
          <div
            className={cn("h-full rounded-full transition-all", color)}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{used?.toFixed(1) || 0} TB used</span>
          <span>{total?.toFixed(1) || 0} TB total</span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* SharePoint */}
      {sharepoint.total_tb && (
        <StorageBar 
          label="SharePoint" 
          used={sharepoint.used_tb || 0} 
          total={sharepoint.total_tb || 1} 
          color="bg-blue-500"
        />
      )}

      {/* OneDrive */}
      {onedrive.total_users && (
        <div className="p-3 rounded-lg bg-muted/30">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">OneDrive</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className="text-lg font-bold text-primary">{onedrive.total_users}</p>
              <p className="text-xs text-muted-foreground">Users</p>
            </div>
            <div>
              <p className="text-lg font-bold text-primary">{onedrive.avg_usage_gb?.toFixed(1) || 0} GB</p>
              <p className="text-xs text-muted-foreground">Avg Usage</p>
            </div>
          </div>
        </div>
      )}

      {/* Exchange */}
      {exchange.total_mailboxes && (
        <div className="p-3 rounded-lg bg-muted/30">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">Exchange</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <p className="text-lg font-bold text-primary">{exchange.total_mailboxes}</p>
              <p className="text-xs text-muted-foreground">Mailboxes</p>
            </div>
            <div>
              <p className="text-lg font-bold text-primary">{exchange.avg_mailbox_size_gb?.toFixed(1) || 0} GB</p>
              <p className="text-xs text-muted-foreground">Avg Size</p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!sharepoint.total_tb && !onedrive.total_users && !exchange.total_mailboxes && (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <p className="text-sm">No storage data available</p>
        </div>
      )}

      {/* Footer */}
      {lastFetched && (
        <div className="pt-2 text-xs text-muted-foreground text-center">
          Updated {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};


/**
 * Tenant Info Widget Content
 * Shows organization/tenant information
 */
export const TenantInfoWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchTenant = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/tenant`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setTenantData(data);
        setLastFetched(new Date());
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch tenant data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  if (isLoading && !tenantData) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-destructive mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="ghost" onClick={fetchTenant} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="space-y-4">
        <div>
          <p className="text-xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {tenantData?.tenant_name || 'Unknown Organization'}
          </p>
          <p className="text-sm text-muted-foreground">
            {tenantData?.primary_domain || 'No domain'}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
            <span className="text-xs text-muted-foreground">Tenant ID</span>
            <span className="text-xs font-mono truncate max-w-[150px]">{tenantData?.tenant_id || '—'}</span>
          </div>
          <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
            <span className="text-xs text-muted-foreground">Region</span>
            <span className="text-xs">{tenantData?.region || '—'}</span>
          </div>
          <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
            <span className="text-xs text-muted-foreground">Data Source</span>
            <span className={cn(
              "text-xs px-2 py-0.5 rounded-full",
              tenantData?.data_source === 'live' 
                ? "bg-green-500/20 text-green-500" 
                : "bg-muted text-muted-foreground"
            )}>
              {tenantData?.data_source || 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      {lastFetched && (
        <div className="mt-auto pt-3 text-xs text-muted-foreground text-center">
          Last refreshed {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};



/**
 * M365 Overview Widget Content (Hero Widget)
 * Combined dashboard showing licenses, users, security, and service health at a glance
 */
export const M365OverviewWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    licenses: null,
    identity: null,
    security: null,
    services: null,
    tenant: null,
  });
  const [lastFetched, setLastFetched] = useState(null);

  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      // Fetch all APIs in parallel
      const [licensesRes, identityRes, securityRes, servicesRes, tenantRes] = await Promise.allSettled([
        fetch(`${API_URL}/api/licenses`),
        fetch(`${API_URL}/api/identity`),
        fetch(`${API_URL}/api/security/posture`),
        fetch(`${API_URL}/api/services/health`),
        fetch(`${API_URL}/api/tenant`),
      ]);

      const parseResponse = async (res) => {
        if (res.status === 'fulfilled' && res.value.ok) {
          return await res.value.json();
        }
        return null;
      };

      const newData = {
        licenses: await parseResponse(licensesRes),
        identity: await parseResponse(identityRes),
        security: await parseResponse(securityRes),
        services: await parseResponse(servicesRes),
        tenant: await parseResponse(tenantRes),
      };

      // Check if we got any data
      const hasAnyData = Object.values(newData).some(d => d && !d.error);
      
      if (!hasAnyData) {
        setError('Unable to fetch M365 data. Please ensure you are signed in with a Microsoft account.');
      } else {
        setData(newData);
      }
      
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message || 'Failed to fetch M365 overview data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  if (isLoading && !lastFetched) {
    return (
      <div className="grid grid-cols-4 gap-4 h-full">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-20" />
          </div>
        ))}
      </div>
    );
  }

  if (error && !data.licenses && !data.identity) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-10 h-10 text-destructive mb-3" />
        <p className="text-sm font-medium text-destructive mb-2">Unable to load M365 Overview</p>
        <p className="text-xs text-muted-foreground mb-4">{error}</p>
        <Button size="sm" variant="outline" onClick={fetchAllData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  // Extract key metrics
  const licenses = data.licenses;
  const identity = data.identity;
  const security = data.security;
  const services = data.services;
  const tenant = data.tenant;

  const totalLicenses = licenses?.licenses?.reduce((sum, l) => sum + l.assigned, 0) || 0;
  const licenseCost = licenses?.total_monthly_cost || 0;
  const totalUsers = identity?.total_users || 0;
  const guestUsers = identity?.guest_users || 0;
  const mfaPercent = security?.mfa_coverage_percent || 0;
  const riskySignins = security?.risky_signins_24h || 0;
  
  const servicesList = services?.services || [];
  const healthyServices = servicesList.filter(s => 
    ['up', 'serviceavailable', 'healthy'].includes(s.status?.toLowerCase())
  ).length;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getMfaColor = () => {
    if (mfaPercent >= 95) return 'text-green-500';
    if (mfaPercent >= 80) return 'text-[rgb(var(--warning))]';
    return 'text-destructive';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tenant Header */}
      {tenant && !tenant.error && (
        <div className="pb-3 mb-3 border-b border-border/50">
          <p className="text-lg font-bold text-primary truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {tenant.tenant_name || 'Your Organization'}
          </p>
          <p className="text-xs text-muted-foreground">{tenant.primary_domain || ''}</p>
        </div>
      )}

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
        {/* Licenses */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-blue-500" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">Licenses</span>
          </div>
          <p className="text-2xl font-bold text-blue-500" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {totalLicenses.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatCurrency(licenseCost)}/mo
          </p>
        </div>

        {/* Users */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Users className="w-4 h-4 text-purple-500" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">Users</span>
          </div>
          <p className="text-2xl font-bold text-purple-500" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {totalUsers.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {guestUsers.toLocaleString()} guests
          </p>
        </div>

        {/* Security */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Shield className="w-4 h-4 text-emerald-500" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">MFA Coverage</span>
          </div>
          <p className={cn("text-2xl font-bold", getMfaColor())} style={{ fontFamily: 'Chivo, sans-serif' }}>
            {mfaPercent.toFixed(0)}%
          </p>
          <p className={cn("text-xs mt-1", riskySignins > 0 ? 'text-destructive' : 'text-muted-foreground')}>
            {riskySignins > 0 ? `${riskySignins} risky sign-ins` : 'No risky sign-ins'}
          </p>
        </div>

        {/* Service Health */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Activity className="w-4 h-4 text-amber-500" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">Services</span>
          </div>
          <p className="text-2xl font-bold text-amber-500" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {healthyServices}/{servicesList.length || '—'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {healthyServices === servicesList.length ? 'All healthy' : 'Issues detected'}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="pt-3 mt-3 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <div className={cn(
            "w-2 h-2 rounded-full",
            error ? "bg-[rgb(var(--warning))]" : "bg-green-500"
          )} />
          {error ? 'Partial data' : 'Live data'}
        </span>
        {lastFetched && (
          <span>Updated {lastFetched.toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
};



/**
 * IT Projects Widget Content
 * Shows project list with status and progress
 */
export const ProjectsWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/projects`);
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      setProjects(data.projects || []);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    const interval = setInterval(fetchProjects, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchProjects]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'on_hold': return 'bg-amber-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-muted-foreground';
    }
  };

  if (isLoading && !projects.length) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-destructive mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="ghost" onClick={fetchProjects} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const activeProjects = projects.filter(p => p.status === 'in_progress').length;

  return (
    <div className="flex flex-col h-full">
      {/* Summary */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-border/50">
        <div>
          <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {activeProjects}
          </p>
          <p className="text-xs text-muted-foreground">Active Projects</p>
        </div>
        <p className="text-sm text-muted-foreground">{projects.length} total</p>
      </div>

      {/* Project List */}
      <div className="flex-1 overflow-auto space-y-2">
        {projects.slice(0, 6).map((project, idx) => (
          <div key={project.id || idx} className="p-3 rounded-lg bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium truncate flex-1 mr-2">{project.name}</span>
              <div className={cn("w-2 h-2 rounded-full", getStatusColor(project.status))} />
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${project.progress_percent || 0}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{project.progress_percent || 0}% complete</p>
          </div>
        ))}
        {projects.length > 6 && (
          <p className="text-xs text-muted-foreground text-center pt-2">
            +{projects.length - 6} more projects
          </p>
        )}
        {projects.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm">No projects found</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {lastFetched && (
        <div className="pt-2 text-xs text-muted-foreground text-center">
          Updated {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};


/**
 * Internal Services Widget Content
 * Shows internal service health monitoring
 */
export const InternalServicesWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchServices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/services/internal`);
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      setServices(data.services || []);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    const interval = setInterval(fetchServices, 60 * 1000); // Check every minute
    return () => clearInterval(interval);
  }, [fetchServices]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'up': return <div className="w-2.5 h-2.5 rounded-full bg-green-500" />;
      case 'down': return <div className="w-2.5 h-2.5 rounded-full bg-destructive" />;
      case 'degraded': return <div className="w-2.5 h-2.5 rounded-full bg-[rgb(var(--warning))]" />;
      default: return <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground" />;
    }
  };

  if (isLoading && !services.length) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-destructive mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="ghost" onClick={fetchServices} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const upCount = services.filter(s => s.status === 'up').length;
  const downCount = services.filter(s => s.status === 'down').length;

  return (
    <div className="flex flex-col h-full">
      {/* Summary */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-border/50">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-xl font-bold text-green-500">{upCount}</p>
            <p className="text-xs text-muted-foreground">Up</p>
          </div>
          <div className="text-center">
            <p className={cn("text-xl font-bold", downCount > 0 ? "text-destructive" : "text-muted-foreground")}>{downCount}</p>
            <p className="text-xs text-muted-foreground">Down</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{services.length} services</p>
      </div>

      {/* Service List */}
      <div className="flex-1 overflow-auto space-y-1">
        {services.map((service, idx) => (
          <div 
            key={service.id || idx} 
            className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {getStatusIcon(service.status)}
              <span className="text-sm truncate">{service.name}</span>
            </div>
            <span className="text-xs text-muted-foreground">
              {service.response_time_ms ? `${service.response_time_ms}ms` : '—'}
            </span>
          </div>
        ))}
        {services.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm">No services configured</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {lastFetched && (
        <div className="pt-2 text-xs text-muted-foreground text-center">
          Updated {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};


/**
 * External Services Widget Content
 * Shows Smartsheet, RingCentral, and other external service metrics
 */
export const ExternalServicesWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/external-services`);
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const result = await response.json();
      setData(result);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-destructive mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="ghost" onClick={fetchData} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Smartsheet */}
      {data?.smartsheet && (
        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Smartsheet</span>
            <span className={cn(
              "text-xs px-2 py-0.5 rounded-full",
              data.smartsheet.status === 'connected' 
                ? "bg-green-500/20 text-green-500" 
                : "bg-muted text-muted-foreground"
            )}>
              {data.smartsheet.status}
            </span>
          </div>
          <p className="text-2xl font-bold text-green-500">{data.smartsheet.users || 0}</p>
          <p className="text-xs text-muted-foreground">Licensed Users</p>
        </div>
      )}

      {/* RingCentral */}
      {data?.ringcentral && (
        <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">RingCentral</span>
            <span className={cn(
              "text-xs px-2 py-0.5 rounded-full",
              data.ringcentral.status === 'connected' 
                ? "bg-orange-500/20 text-orange-500" 
                : "bg-muted text-muted-foreground"
            )}>
              {data.ringcentral.status}
            </span>
          </div>
          <p className="text-2xl font-bold text-orange-500">{data.ringcentral.extensions || 0}</p>
          <p className="text-xs text-muted-foreground">Extensions</p>
        </div>
      )}

      {!data?.smartsheet && !data?.ringcentral && (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <p className="text-sm">No external services configured</p>
        </div>
      )}

      {/* Footer */}
      {lastFetched && (
        <div className="pt-2 text-xs text-muted-foreground text-center">
          Updated {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};


/**
 * Jira Operations Widget Content
 * Shows Jira tickets, SLA metrics, and satisfaction
 */
export const JiraOperationsWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetchWithTimeout(`${API_URL}/api/operations?days=7`, {}, 25000);
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const result = await response.json();
      
      // Check if result indicates an error from the backend
      if (result.source === 'error' || result.tickets?.source === 'error') {
        const errorMsg = result.tickets?.error || result.error || 'Jira connection failed';
        setError(errorMsg);
        setData(null); // Clear any stale data
        setLastFetched(null); // Clear timestamp to avoid confusion
        return;
      }
      
      // Validate that we got meaningful data
      const ticketData = result.tickets || result;
      if (!ticketData || ticketData.source === 'error') {
        setError('No data available from Jira');
        setData(null);
        setLastFetched(null);
        return;
      }
      
      setData(ticketData);
      setLastFetched(new Date());
    } catch (err) {
      console.error('[JiraWidget] Fetch error:', err);
      setError(err.message || 'Failed to load data');
      setData(null);
      setLastFetched(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (isLoading && !data) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-amber-500 mb-2" />
        <p className="text-sm text-amber-600 mb-1">Unable to load Jira data</p>
        <p className="text-xs text-muted-foreground mb-3">
          {error.includes('timed out') ? 'Connection to Jira server timed out' : error}
        </p>
        <Button size="sm" variant="ghost" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  // Extract metrics from the Jira response structure
  const openTickets = data?.totals?.open || 0;
  const closedTickets = data?.totals?.closed || 0;
  
  // Get satisfaction percentage
  const satisfactionPercent = data?.satisfaction?.satisfaction_percent;

  return (
    <div className="flex flex-col h-full">
      {/* Metrics Grid - 3 columns */}
      <div className="grid grid-cols-3 gap-3 flex-1">
        <div className="p-4 rounded-lg bg-blue-500/10 text-center flex flex-col justify-center">
          <p className="text-3xl font-bold text-blue-500">{openTickets}</p>
          <p className="text-xs text-muted-foreground mt-1">Open Tickets</p>
        </div>
        <div className="p-4 rounded-lg bg-green-500/10 text-center flex flex-col justify-center">
          <p className="text-3xl font-bold text-green-500">{closedTickets}</p>
          <p className="text-xs text-muted-foreground mt-1">Resolved (7 days)</p>
        </div>
        <div className="p-4 rounded-lg bg-purple-500/10 text-center flex flex-col justify-center">
          <p className="text-3xl font-bold text-purple-500">
            {satisfactionPercent != null ? `${satisfactionPercent}%` : '—%'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Satisfaction</p>
        </div>
      </div>

      {/* Footer */}
      {lastFetched && (
        <div className="pt-3 text-xs text-muted-foreground text-center">
          Updated {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};


/**
 * Goals Tracker Widget Content
 * Shows 2026 IT Goals progress
 */
export const GoalsTrackerWidgetContent = ({ config, onConfigChange }) => {
  const [progress, setProgress] = useState({ percent: 0, completed: 0, total: 0, topics: [] });

  useEffect(() => {
    const calculateProgress = () => {
      try {
        const saved = localStorage.getItem('it_goals_2026');
        if (saved) {
          const topics = JSON.parse(saved);
          const totalInitiatives = topics.reduce((sum, t) => sum + (t.initiatives?.length || 0), 0);
          const completedInitiatives = topics.reduce(
            (sum, t) => sum + (t.initiatives?.filter(i => i.status === 'complete').length || 0),
            0
          );
          const percent = totalInitiatives > 0 ? Math.round((completedInitiatives / totalInitiatives) * 100) : 0;
          setProgress({ percent, completed: completedInitiatives, total: totalInitiatives, topics });
        }
      } catch {
        // Ignore parse errors
      }
    };
    
    calculateProgress();
    const interval = setInterval(calculateProgress, 2000);
    window.addEventListener('storage', calculateProgress);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', calculateProgress);
    };
  }, []);

  const getColor = () => {
    if (progress.percent >= 70) return 'text-green-500';
    if (progress.percent >= 40) return 'text-[rgb(var(--warning))]';
    return 'text-primary';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Main Progress */}
      <div className="text-center mb-4 pb-4 border-b border-border/50">
        <p className={cn("text-4xl font-bold", getColor())} style={{ fontFamily: 'Chivo, sans-serif' }}>
          {progress.percent}%
        </p>
        <p className="text-sm text-muted-foreground">
          {progress.completed}/{progress.total} initiatives complete
        </p>
        <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all", getColor().replace('text-', 'bg-'))}
            style={{ width: `${progress.percent}%` }}
          />
        </div>
      </div>

      {/* Topic Breakdown */}
      <div className="flex-1 overflow-auto space-y-2">
        {progress.topics.slice(0, 5).map((topic, idx) => {
          const topicTotal = topic.initiatives?.length || 0;
          const topicComplete = topic.initiatives?.filter(i => i.status === 'complete').length || 0;
          const topicPercent = topicTotal > 0 ? Math.round((topicComplete / topicTotal) * 100) : 0;
          
          return (
            <div key={topic.id || idx} className="p-2 rounded-lg bg-muted/30">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium truncate flex-1 mr-2">{topic.title}</span>
                <span className="text-xs text-muted-foreground">{topicPercent}%</span>
              </div>
              <div className="h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full"
                  style={{ width: `${topicPercent}%` }}
                />
              </div>
            </div>
          );
        })}
        {progress.topics.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm">No goals configured</p>
            <p className="text-xs mt-1">Add goals in the Goals Tracker section</p>
          </div>
        )}
      </div>
    </div>
  );
};


/**
 * Expenses Widget Content
 * Shows additional IT expenses
 */
export const ExpensesWidgetContent = ({ config, onConfigChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchExpenses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/metrics/custom`);
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      // Filter for expense-type metrics
      const expenseMetrics = (data.metrics || []).filter(m => 
        m.category === 'expense' || m.type === 'currency'
      );
      setExpenses(expenseMetrics);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
    const interval = setInterval(fetchExpenses, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchExpenses]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  if (isLoading && !expenses.length) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-4">
        <AlertCircle className="w-8 h-8 text-destructive mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="ghost" onClick={fetchExpenses} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const totalExpenses = expenses.reduce((sum, e) => sum + (parseFloat(e.value) || 0), 0);

  return (
    <div className="flex flex-col h-full">
      {/* Total */}
      <div className="text-center mb-3 pb-3 border-b border-border/50">
        <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {formatCurrency(totalExpenses)}
        </p>
        <p className="text-xs text-muted-foreground">Total Additional Expenses</p>
      </div>

      {/* Expense List */}
      <div className="flex-1 overflow-auto space-y-2">
        {expenses.map((expense, idx) => (
          <div key={expense.id || idx} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
            <span className="text-sm truncate flex-1 mr-2">{expense.label || expense.name}</span>
            <span className="text-sm font-medium">{formatCurrency(expense.value)}</span>
          </div>
        ))}
        {expenses.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm">No expenses tracked</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {lastFetched && (
        <div className="pt-2 text-xs text-muted-foreground text-center">
          Updated {lastFetched.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};
